import { Badge } from '@/components/ui/badge'
import type { TranslationState } from '@/features/i18n/api'

/**
 * One definition of what each translation state looks like, used by both the
 * catalog list and the editor.
 *
 * The wording matters more than usual here. "Not translated" is the state that
 * fails silently under D-7: the key still ships, carrying the English string, so
 * an untranslated Japanese screen renders readable English and looks
 * intentional. Labelling it "ships English" rather than leaving it blank is the
 * only thing on screen that says otherwise.
 */
const PRESENTATION: Record<
  TranslationState,
  { label: string; variant: 'success' | 'accent' | 'warning' | 'default' | 'destructive'; title: string }
> = {
  human: {
    label: 'Human',
    variant: 'success',
    title: 'A person wrote or approved this. Automated translation will never overwrite it.',
  },
  machine: {
    label: 'Machine',
    variant: 'accent',
    title: 'Machine draft, nobody has reviewed it. Refreshed whenever the English changes.',
  },
  needs_review: {
    label: 'Needs review',
    variant: 'warning',
    title:
      'The English changed underneath this translation, or a placeholder check was overridden. The content is untouched.',
  },
  missing: {
    label: 'Ships EN',
    variant: 'default',
    title: 'No translation — this key ships the English string, which readers may take as intentional.',
  },
  // PRD §3.1. Ships English exactly like `missing`, but the last machine attempt
  // failed, so pressing Fill missing again is the fix rather than waiting.
  failed: {
    label: 'Translation failed',
    variant: 'destructive',
    title:
      'The last machine translation failed — this key ships the English string. Retranslate the row, or use Fill missing.',
  },
}


export function StatusBadge({ status }: { status: TranslationState }) {
  const { label, variant, title } = PRESENTATION[status]
  return (
    <Badge variant={variant} title={title}>
      {label}
    </Badge>
  )
}

/** Compact per-language chip for the catalog list: `JA Human`. */
export function LanguageStatus({ code, status }: { code: string; status: TranslationState }) {
  const { variant, title } = PRESENTATION[status]
  return (
    <Badge variant={variant} title={`${code} — ${title}`} className="gap-1">
      <span className="font-mono opacity-70">{code}</span>
    </Badge>
  )
}
